import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import './sheetStyle.css';

/**
 * Sheet — the ONE reusable bottom-sheet chrome (David 2026-08-04). Every closeable bottom sheet in the
 * app (Live + playback) renders through this so they are consistent: a scrim, the drag grip, an optional
 * heading, a top-right close ✕, and the body. Consolidates the ad-hoc `.live-sheet` / `.mcpb-sheet`
 * markup that had drifted (some sheets had the ✕, some didn't).
 *
 * BREAKPOINT-AGNOSTIC: this owns the STRUCTURE only. Each call site keeps its existing outer class(es)
 * via `className` (e.g. "live-sheet live-tile-sheet" or "mcpb-sheet mcpb-sheet--views"), so the current
 * bottom-sheet positioning, animation, theming, AND the media queries that decide WHERE each sheet shows
 * are all preserved untouched — the migration changes structure, not styling/breakpoints.
 *
 * The close ✕ sits in the true upper-right CORNER (absolute, nudged up + right of the heading — David),
 * not vertically centered across from the title. It renders only when `onClose` is provided (a sheet that
 * requires a selection can omit it); the scrim also dismisses via `onClose`.
 *
 * Props:
 *   className     — outer panel class(es); KEEP the existing ones for styling + breakpoint continuity
 *   scrimClassName— scrim class (default "app-sheet-scrim"; pass the existing one to keep its styling)
 *   title         — optional heading text (rendered in .app-sheet__title). Omit for a heading-less sheet.
 *   ariaLabel     — dialog accessible name (falls back to `title`)
 *   onClose       — dismiss handler; when set, wires the ✕ + the scrim click. Omit → no ✕ (selection-required).
 *   closeLabel    — aria-label for the ✕ (default "Close")
 *   grip          — render the drag grip (default true)
 *   scrim         — render the dismiss scrim (default true; pass false for a panel that has no scrim —
 *                   e.g. a fixed sub-panel whose z-index sits below a full-screen scrim)
 *   scrimTestId   — data-testid on the scrim (kept for existing tests)
 *   role          — dialog role (default "dialog")
 *   children      — the sheet body
 */
const Sheet = ({
  className = 'app-sheet',
  scrimClassName = 'app-sheet-scrim',
  title,
  ariaLabel,
  onClose,
  closeLabel = 'Close',
  grip = true,
  scrim = true,
  scrimTestId,
  role = 'dialog',
  // Optional CSS selector of an ancestor to PORTAL into (default: render inline, unchanged). Some hosts
  // wrap the sheet in a stacking context that traps the fixed scrim below sibling content — e.g. the
  // playback bottom bar's frosted `.mcpb-tools-pill` (backdrop-filter) left the Filters/Options scrim
  // under the grid overlays + timeline. Portaling to a scoped top-level node (`.mcplayback-container`)
  // escapes that context while KEEPING the scoped CSS (the selector is still an ancestor). Opt-in so
  // Live sheets + existing tests are untouched. (#86bbmxrtt, David 2026-08-28.)
  portalSelector,
  children,
  ...rest
}) => {
  // Escape-to-close (keyboard parity with the ✕ + scrim). Only when the sheet is dismissable (has onClose);
  // a selection-required sheet that omits onClose also can't be Esc-dismissed.
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Resolve an explicitly requested portal during render so stateful children mount once. Playback
  // menu callers already own their portal placement; inferring another portal from a CSS class would
  // move and remount the same sheet after first paint.
  const portalHost = portalSelector && typeof document !== 'undefined'
    ? document.querySelector(portalSelector) : null;

  const content = (
  <>
    {scrim && <div className={scrimClassName} onClick={onClose} data-testid={scrimTestId} />}
    <div className={className} role={role} aria-label={ariaLabel || title} {...rest}>
      {grip && <div className="app-sheet__grip" />}
      {onClose && (
        <button type="button" className="app-sheet__close" onClick={onClose} aria-label={closeLabel}>
          &times;
        </button>
      )}
      {title && <div className="app-sheet__title">{title}</div>}
      {children}
    </div>
  </>
  );
  return portalHost ? createPortal(content, portalHost) : content;
};

export default Sheet;
